import { BehaviorSubject } from "rxjs";
import { QueryType, QueryFields } from "data/Types";
import produce from "immer";

import { fetchWrapper } from "data/FetchWrapper";
import { ResponseType, StatusType, DataSource } from "data/Types";
import { detailService, alertService } from "services";
import queryString from "query-string";
import { history } from "data/History";

const initialState = {
  type: QueryType.ALL,
  fields: QueryFields.Default,
  query: "",
  isAdvanced: false,
};

const search$ = new BehaviorSubject({ ...initialState });
const result$ = new BehaviorSubject({});
const status$ = new BehaviorSubject(StatusType.None);
const abort$ = new BehaviorSubject(new AbortController());
const baseUrl = `/api/search`;

const PAGE_SIZE = 20;
const MAX_PAGE = 100;

const reset = () => {
  abort();
  history.push("/");
  status$.next(StatusType.None);
  search$.next({ ...initialState });
  // To reset advanced queries we need to reset twice!
  setImmediate(() => search$.next({ ...initialState }));
  result$.next({});
};

const mapResult = (response) => {
  return {
    summary: response.report.summary,
    docsCount: response.report.docsCount,
    resultset: response.report.data.map(mapDocument),
  };
};

const mapDocument = (document) => {
  const d = document.document;
  const authors = d.dataSource !== DataSource.patstat && d.authors;
  const owners = (d.dataSource === DataSource.patstat && d.authors) || d.owners;
  const classification =
    d.dataSource === DataSource.patstat || d.dataSource === DataSource.uspto
      ? d["classification-ipcr"] || d.applnIpc || undefined
      : undefined;
  return {
    id: d._id,
    patentNumber: d.number,
    title: d.title || (d.applnTitle && d.applnTitle[0].title),
    abstract: d.abstract || (d.applnAbstr && d.applnAbstr[0].abstract),
    date: d.date,
    year: d.year,
    responseType: ResponseType[d.dataCategory],
    dataSource: d.dataSource,
    authors: authors ? mapPersons(authors) : undefined,
    owners: owners ? mapPersons(owners) : undefined,
    ipc: classification ? mapIPC(classification) : undefined,
    cpc: classification ? mapCPC(classification) : undefined,
    country: d.country,
    language: d.lang,
    status: d.status || d.granted,
    publisher: d.publisher,
    fos: d.fos,
    keywords: d.keywords,
    issue: d.issue,
    url: d.url && [...new Set(d.url)],
    doi: d.doi,
    venue: d.venue,
    potentiallyLicensed: d.potentiallyLicensed,
    potentiallyLicensable: d.potentiallyLicensable,
  };
};

const mapCPC = (classification) => {
  return classification.hasOwnProperty("classification-cpc-text")
    ? Array.isArray(classification["classification-cpc-text"])
      ? classification["classification-cpc-text"]
      : [classification["classification-cpc-text"]]
    : undefined;
};

const mapIPC = (classification) => {
  return Array.isArray(classification)
    ? classification.map((c) => c.ipc_class_symbol)
    : undefined;
};

const mapPersons = (persons) => {
  return persons.map((p) => ({
    name: p.name || p.person_name,
  }));
};

const abortSearch = () => {
  let status = status$.value | StatusType.Result | StatusType.Queried;
  status &= ~StatusType.Querying & ~StatusType.Modified & ~StatusType.Append;
  const result = produce(result$.value, (result) => {
    if (!result.resultset) {
      result.resultset = [];
    }
  });
  status$.next(status);
  result$.next(result);
};

const abort = (search = false) => {
  detailService.abort();
  abort$.value.abort();
  abort$.next(new AbortController());
  search && abortSearch();
};

const handleReportGenerationError = (message) => {
  if (
    message.startsWith("QueryFields") ||
    message.startsWith("SearchRequest is not valid") ||
    message.startsWith("Cannot query Report Generation Module")
  ) {
    return message;
  }
  return "Report Generation Module has connectivity problems. Please try again later.";
};

const execute = (page = 1, append = false) => {
  if (page > 100) {
    alertService.error("Report Generation Module pagination out of range.");
    return;
  }
  const search = search$.value;
  const params = {
    type: search.type,
    q: search.query,
  };
  if (search.isAdvanced) {
    params.advanced = null;
  } else {
    params.fields = search.fields;
  }
  history.push(`/?${queryString.stringify(params)}`);
  const result = result$.value;
  let status = status$.value;
  status |= StatusType.Queried | StatusType.Querying;
  if (append) {
    if (status & StatusType.Modified) {
      alertService.warn(
        "Your search parameters have changed. Please search again to load more results."
      );
      return;
    }
    status |= StatusType.Append;
  } else {
    abort();
    status &= ~StatusType.Modified;
    status &= ~StatusType.Append;
  }
  status$.next(status);
  return fetchWrapper
    .post(`${baseUrl}?page=${page}`, search, abort$.value.signal)
    .then((response) => {
      let _result = produce(mapResult(response), (_result) => {
        if (_result.docsCount > MAX_PAGE * PAGE_SIZE) {
          if (!append) {
            console.warn(
              `Report Generation Module does not allow more than ${PAGE_SIZE} pages, reducing results to ${
                MAX_PAGE * PAGE_SIZE
              }`
            );
            alertService.warn(
              `Found ${_result.docsCount.toLocaleString()} results. The ${(
                MAX_PAGE * PAGE_SIZE
              ).toLocaleString()} most relevant results have been identified. Please refine your search to achieve more accuracy`
            );
          }
          _result.docsCount = MAX_PAGE * PAGE_SIZE;
        }
        const index = page * PAGE_SIZE - PAGE_SIZE;
        const resultset = _result.resultset;
        if (append && result && result.resultset) {
          _result.resultset = [...result.resultset];
        } else {
          _result.resultset = new Array(_result.docsCount);
        }
        _result.resultset.splice(index, resultset.length, ...resultset);
      });
      result$.next(_result);
      status |= StatusType.Result;
      status &= ~StatusType.Querying;
      status$.next(status);
      search$.next(search);
      return _result;
    })
    .catch((e) => {
      if (e.name === "AbortError" || e instanceof DOMException) {
        console.warn(e);
        return;
      }
      alertService.error(handleReportGenerationError(e.message));
      status |= StatusType.Result;
      status &= ~StatusType.Querying;
      const r = produce(result, (result) => {
        if (!result.resultset) {
          result.resultset = [];
        }
      });
      result$.next(r);
      status$.next(status);
    });
};

export const searchService = {
  reset,
  execute,
  search$: search$,
  result$: result$,
  status$: status$,
  get searchValue() {
    return search$.value;
  },
  get resultValue() {
    return result$.value;
  },
  abort,
  mapper: {
    result: mapResult,
    document: mapDocument,
    persons: mapPersons,
  },
};
